import { Link } from "react-router-dom";
import { ArrowRight, Quote, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import SectionHeader from "@/components/site/SectionHeader";
import { FounderMedallion } from "@/components/site/FounderCard";
import PhilosophyList from "@/components/site/PhilosophyList";
import { BlobA, BlobB } from "@/components/site/BlobBackground";
import { FOUNDERS } from "@/lib/siteConfig";

export default function AboutPage() {
  return (
    <>
      <section className="relative overflow-hidden hero-wash">
        <BlobA className="absolute -top-20 -right-20 w-[400px] h-[400px] hidden sm:block" />
        <div className="container-narrow relative py-16 sm:py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.22em] text-terracotta font-semibold" data-testid="about-eyebrow">About us</div>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.05] text-ink" data-testid="about-title">
              The real human experiences — taught by practising the work.
            </h1>
            <p className="mt-5 text-ink-muted text-lg leading-[1.75] max-w-2xl" data-testid="about-subtitle">
              The Human Work Project is a structured, practice-oriented psychology and mental-health program —
              built on 8 years of Therapist Tribe. We bring together training, guided practice, community-based
              field work, supervision, and reflection — for people learning how to truly be with others.
            </p>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="container-narrow py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeader
              eyebrow="Program philosophy"
              title="Five quiet beliefs that shape this work."
              subtitle="Everything we design — the modules, the field work, the supervision spaces — sits on these."
              testidPrefix="philosophy"
            />
          </div>
          <div className="lg:col-span-7">
            <PhilosophyList />
          </div>
        </div>
      </section>

      {/* TEAM DETAILED */}
      <section className="section-warm">
        <div className="container-narrow py-16 sm:py-20 lg:py-24">
          <SectionHeader
            eyebrow="The team"
            title="Led by Astha. Built with practitioners."
            subtitle="Astha leads The Human Work Project. Ruhika, Aditi, and a wider circle of practitioners bring decades of combined clinical practice, training, and community work."
            testidPrefix="team"
          />
          <div className="mt-12 space-y-16">
            {FOUNDERS.map((f) => (
              <FounderDetail key={f.slug} founder={f} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-narrow py-16 sm:py-20">
        <div className="relative overflow-hidden rounded-3xl bg-paper border border-[rgba(74,63,57,0.10)] p-8 sm:p-12 lg:p-14">
          <BlobB className="absolute -top-10 -right-10 w-[300px] h-[300px] hidden sm:block" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="text-[11px] uppercase tracking-[0.22em] text-terracotta font-semibold">Cohort 02 · 1 December 2026</div>
              <h3 className="mt-2 font-display text-3xl sm:text-4xl text-ink leading-[1.1]">
                Ready to learn the human side of psychology?
              </h3>
              <p className="mt-3 text-ink-muted leading-[1.75] max-w-xl">
                Two tracks, one cohort. Hybrid format, real fieldwork, mentorship, and a small, considered group of peers.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right space-y-3">
              <Link to="/course" className="block">
                <Button data-testid="about-cta-foundation" className="bg-[#C86A4A] hover:bg-[#B85F41] text-white rounded-xl px-6 py-6 text-base transition-colors w-full sm:w-auto">
                  Foundation Track <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/practice-track" className="block">
                <Button data-testid="about-cta-practice" className="bg-[#5F7F6B] hover:bg-[#506e5c] text-white rounded-xl px-6 py-6 text-base transition-colors w-full sm:w-auto">
                  Advanced Practice Track <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function FounderDetail({ founder: f }) {
  return (
    <article
      className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10"
      data-testid={`founder-detail-${f.slug}`}
    >
      <aside className="lg:col-span-4">
        <div className="lg:sticky lg:top-28">
          <FounderMedallion initials={f.initials} slug={f.slug} />
          <h3 className="mt-4 font-display text-2xl sm:text-3xl text-ink">{f.name}</h3>
          <p className="mt-1 text-sm text-ink-muted">{f.role}</p>
          {f.experience && (
            <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[rgba(126,155,134,0.22)] text-moss text-xs font-semibold">
              {f.experience}
            </div>
          )}
          {f.philosophy && (
            <div className="mt-6 bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-5">
              <Quote className="w-5 h-5 text-terracotta mb-2" />
              <p className="text-sm text-ink leading-[1.75] italic">{f.philosophy}</p>
            </div>
          )}
        </div>
      </aside>
      <div className="lg:col-span-8 space-y-6">
        {(f.bio || [f.intro]).map((para, idx) => (
          <p key={idx} className="text-ink leading-[1.85] text-[15.5px]">
            {para}
          </p>
        ))}

        {f.highlights && f.highlights.length > 0 && (
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.22em] text-terracotta font-semibold mb-3">Highlights</h4>
            <div className="flex flex-wrap gap-2">
              {f.highlights.map((h) => (
                <span
                  key={h}
                  className="text-[12.5px] px-3 py-1.5 rounded-full bg-[rgba(200,106,74,0.10)] text-[#8a4128] border border-[rgba(200,106,74,0.18)]"
                >
                  {h}
                </span>
              ))}
            </div>
          </div>
        )}

        {f.workshops && f.workshops.length > 0 && (
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.22em] text-moss font-semibold mb-3">
              Workshops & training conducted
            </h4>
            <ul className="space-y-3">
              {f.workshops.map((w) => (
                <li
                  key={w.title}
                  className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-5"
                >
                  <div className="flex items-start gap-3">
                    <Star className="w-4 h-4 text-moss shrink-0 mt-1" />
                    <div>
                      <div className="font-display text-base text-ink leading-snug">{w.title}</div>
                      <p className="mt-1.5 text-sm text-ink-muted leading-[1.7]">{w.text}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  );
}
